<template>
    <v-dialog :value="show" width="900" scrollable
        @input="$emit('update:show', $event)">
        <div class="mp">
            <div class="mp-head">
                <div>
                    <div class="mp-title">{{ $t('media.picker.title') }}</div>
                    <div class="mp-sub">{{ purposeLabel }}</div>
                </div>
                <v-btn icon small @click="close">
                    <v-icon size="20">mdi-close</v-icon>
                </v-btn>
            </div>

            <!-- tab 與「新增素材」的入口同一列：挑不到想要的就在原地新增，
                 不必關掉對話框再從別的地方進去。 -->
            <div class="mp-tabrow">
                <!-- ⚠ 三個 tab，沒有「團隊」——team 共用已於 2026-08-20 取消（規格 §2.3） -->
                <v-tabs v-model="tabIndex" height="42" class="mp-tabs">
                    <v-tab v-for="s in scopes" :key="s" class="normal-case">
                        {{ $t(`media.scope.${s}`) }}
                    </v-tab>
                </v-tabs>

                <div class="mp-actions">
                    <!-- ⚠ 字表沒有這一顆：它沒有檔案可上傳，內容在 attributes.words
                         （規格 §2.2）。它有自己的入口，見下面的「建立字表」。 -->
                    <button v-if="canUpload" type="button" class="mp-act"
                        @click="openUpload('file')">
                        {{ $t('media.picker.upload') }}
                    </button>
                    <!-- ⚠ 字表是**建立**而不是上傳：沒有檔案，走 JSON 端點、不佔配額、
                         不轉檔。兩顆按鈕互斥（`canUpload` 與 `canCreate` 不會同時成立），
                         所以 tab 列永遠只多一顆。 -->
                    <button v-if="canCreate" type="button" class="mp-act"
                        @click="wordlistOpen = true">
                        {{ $t('media.wordlist.create') }}
                    </button>
                    <!-- ⚠ YouTube 是「不提供貼網址」原則的唯一例外（2026-08-12 的版權決定）：
                         它有獨立端點、不上傳也不轉檔，只把正規化後的網址登錄成一筆素材。 -->
                    <button v-if="purpose === 'video'" type="button" class="mp-act"
                        @click="openUpload('youtube')">
                        {{ $t('media.picker.use_youtube') }}
                    </button>
                </div>
            </div>

            <div class="mp-filters">
                <v-text-field v-model="keyword" dense hide-details outlined clearable
                    prepend-inner-icon="mdi-magnify" class="mp-search"
                    :placeholder="$t('media.picker.search_hint')"
                    @keyup.enter="reload(1)" @click:clear="reload(1)" />
                <!-- ⚠ **語系過濾整個拿掉了**（2026-09-01）。原本只有字表顯示它，因為圖片／
                     PDF／影片的三條寫入路徑一律寫 NULL（＝通用素材）、過濾不到任何東西。
                     現在字表也不分語系（只支援英文，建立時不送 `lang`），於是**沒有任何
                     purpose 用得上它**。
                     ⚠ 而它不是「留著也無害」：手動 insert 的官方素材**可能有 lang**
                     （實測 stage 上有兩筆 `lang='en'` 的 PDF），送了 lang 之後那些列會在
                     介面語言不符時憑空消失，而老師沒有控制項可以關掉——留著只會製造
                     那個 bug。要恢復語系過濾的話得先有一條會寫 lang 的路徑。 -->

                <!-- ⚠ **來源篩選只有影片有**：`provider` 只寫在影片素材上，別的 purpose
                     放它就是一個永遠篩不到東西的控制項。
                     ⚠ 「上傳」這一項**包含沒有 `provider` 的素材**（手動 insert 的官方
                     影片就是那個形狀），否則一按篩選官方影片會整批消失——語系過濾
                     就是因為同一個原因被整個拿掉的。 -->
                <v-btn-toggle v-if="isVideoPurpose" v-model="provider" dense borderless
                    class="mp-provider" @change="reload(1)">
                    <v-btn value="" small class="normal-case">
                        {{ $t('media.library.provider_all') }}
                    </v-btn>
                    <!-- ⚠ 篩選用中性的「上傳的影片」而不是「自行上傳」：這是**來源類型**
                         的篩選，在「官方」與「公開」分頁裡那些影片並不是老師自己上傳的。
                         每一列的標籤才講擁有者（見上面的 providerLabelKey）。 -->
                    <v-btn value="uploaded" small class="normal-case">
                        {{ $t('media.provider.uploaded') }}
                    </v-btn>
                    <v-btn value="youtube" small class="normal-case">
                        {{ $t('media.provider.youtube') }}
                    </v-btn>
                </v-btn-toggle>
            </div>

            <div class="mp-body">
                <div v-if="isLoading" class="mp-state">
                    <v-progress-circular indeterminate size="30" color="#1e88e5" />
                </div>

                <!-- ⚠ 失敗一定要看得見，而且要能重試——不是靜默無反應（規格 §9） -->
                <div v-else-if="loadError" class="mp-state">
                    <v-icon size="34" color="#e57373">mdi-alert-circle-outline</v-icon>
                    <div class="mp-state-msg">{{ loadError }}</div>
                    <v-btn small outlined class="normal-case" @click="reload(page)">
                        {{ $t('media.picker.retry') }}
                    </v-btn>
                </div>

                <div v-else-if="!items.length" class="mp-state">
                    <v-icon size="34" color="#bdbdbd">mdi-image-off-outline</v-icon>
                    <div class="mp-state-msg">{{ emptyMessage }}</div>
                </div>

                <div v-else class="mp-grid">
                    <!-- ⚠ 非 `ready` 的素材**顯示但不可選**（規格 §6.3）：老師剛上傳的影片
                         要等轉檔完才能用，但如果它在選擇器裡完全不出現，老師會以為上傳失敗。
                         `disabled` 而不是隱藏，是為了讓「它在、只是還沒好」看得出來。 -->
                    <!-- ⚠ **外面這一層 `.mp-cell` 是必要的。** 卡片本身是 `<button>`
                         （點了就選），而 HTML 不允許 `<a>` 巢在 `<button>` 裡面——
                         所以「開啟」必須是它的**兄弟節點**，用絕對定位疊在縮圖角落。
                         改成 `<div role="button">` 也可以，但那會失去 `disabled`
                         的原生語意（轉檔失敗的素材靠它擋住點選）。 -->
                    <div v-for="item in items" :key="item.media_id" class="mp-cell">
                    <button type="button"
                        class="mp-card"
                        :class="{
                            'mp-card--on': item.media_id === value,
                            'mp-card--off': !isSelectable(item),
                        }"
                        :disabled="!isSelectable(item)"
                        @click="pick(item)">
                        <!-- ⚠ 熱區與字表用 --free（高度由內容決定），其餘用固定比例的縮圖框。
                             同一個挑選器裡 purpose 是固定的，所以不會混在一起、版面仍然整齊。 -->
                        <!-- ⚠ 縮圖的算繪邏輯集中在 MediaThumb（2026-09-07 抽出）。
                             這裡打開三個選項，維持既有外觀：
                             - `showTargets`：熱區圖要看得到有幾個框、在哪裡，
                               否則老師無法判斷這張圖適不適合他的班級（規格 §7.2）。
                             - `showWords`：字表沒有檔案可看，只能列單字。
                             - `showFileName`：影片／PDF 沒有縮圖時用圖示 + 檔名。
                             ⚠ **`allowEmbed` 絕對不能打開**：`<embed>` 會下載整個 PDF，
                             而這裡一頁 24 筆 ＝ 最多 480MB 才畫出一頁縮圖。
                             ⚠ `fit` 依分支不同：熱區與字表必須 `free`（熱區的百分比座標
                             相對於圖片本身，contain 會讓框整體偏移）；其餘用 `contain`
                             （老師要看到整張圖才選得準）。 -->
                        <div class="mp-thumb"
                            :class="{ 'mp-thumb--free': usesFreeFit(item) }"
                            :style="usesFreeFit(item) ? null : thumbRatioStyle">
                            <!-- mp-fill 是必要的而不是裝飾：.mp-thumb 用 padding-top 撐出
                                 比例，content 高度是 auto，所以 MediaThumb 的 height:100%
                                 解析不出來、圖片會掉到 padding 底下。見 .mp-fill 的說明。 -->
                            <MediaThumb :media="item"
                                :class="{ 'mp-fill': !usesFreeFit(item) }"
                                :fit="usesFreeFit(item) ? 'free' : 'contain'"
                                show-targets show-words show-file-name
                                :icon-size="34" />

                            <v-icon v-if="item.media_id === value" class="mp-check" size="22"
                                color="#1e88e5">
                                mdi-check-circle
                            </v-icon>
                        </div>
                        <div class="mp-name">{{ item.title }}</div>
                        <!-- ⚠ 需求方 2026-09-21：素材庫看不出圖片尺寸。
                             框的比例已經表達「會被畫成什麼形狀」，這一行補的是
                             「這張圖本身是什麼」——兩個一起看才知道會不會被裁。
                             ⚠ 舊素材的 width/height 可能是 null，沒有就整行不出現。 -->
                        <div v-if="item.width && item.height" class="mp-dim">
                            {{ item.width }}×{{ item.height }}
                        </div>
                        <!-- 轉檔中的也顯示狀態（但可以選）；失敗的用紅字 -->
                        <div v-if="item.process_status && item.process_status !== 'ready'"
                            class="mp-status"
                            :class="{ 'mp-status--bad': item.process_status === 'failed' }">
                            {{ processLabel(item) }}
                        </div>
                        <!-- ⚠ 計數放在圖外面的獨立一行，不要蓋在圖上：疊在左下角會剛好
                             遮住那個位置的熱區，而那正是老師要看的東西。 -->
                        <div v-if="isHotspot(item)" class="mp-meta">
                            {{ $t('media.picker.target_count', { n: targetsOf(item).length }) }}
                        </div>
                        <div v-else-if="isWordlist(item)" class="mp-meta">
                            {{ $t('media.picker.word_count', { n: wordsOf(item).length }) }}
                        </div>
                        <!-- 影片標出來源：YouTube 與上傳的檔案對老師的意義不同
                             （前者不佔配額、要能連外網；後者反之）。
                             ⚠ 走 `providerLabelKey` 而不是 `providerOf`：同一個
                             `provider = 's3'` 在三個分頁要講三句不同的話——官方提供／
                             自行上傳／他人上傳。2026-09-08 之前一律講「自行上傳」。 -->
                        <div v-else-if="providerLabelKey(item)" class="mp-meta">
                            {{ $t(`media.provider.${providerLabelKey(item)}`) }}
                        </div>
                    </button>

                    <!-- 在新分頁開啟原始檔案。**挑選前的「預覽」就是這個**（2026-09-04）。
                         ⚠ **PDF 最需要它**：縮圖只看得到第一頁的頁首，兩份格式相同的
                         學習單分不出來。圖片與影片也給，一致比特例好。
                         ⚠ 字表沒有 `url`（內容在 `attributes.words`，挑選器已經直接列出
                         單字了），所以不顯示——給連結只會開出一個空白分頁。
                         ⚠ `@click.stop` 不能省：這個連結疊在卡片上，不擋住冒泡的話
                         點「開啟」會同時把這一筆選走並關掉挑選器。 -->
                    <!-- ⚠ **熱區圖走站內預覽，不開新分頁**（2026-09-04）：新分頁開的是
                         原始圖檔，那上面看不到任何熱區——而熱區正是老師要確認的東西。
                         ⚠ `@click.stop` 兩條路都不能省：這個按鈕疊在卡片上，
                         不擋住冒泡的話點它會同時把這一筆選走並關掉挑選器。 -->
                    <button v-if="isHotspot(item)" type="button" class="mp-open"
                        :title="$t('media.hotspot.preview_action')"
                        @click.stop="openPreview(item)">
                        <v-icon size="15" color="#37474f">mdi-magnify-expand</v-icon>
                    </button>
                    <a v-else-if="item.url" :href="item.url" target="_blank" rel="noopener"
                        class="mp-open" :title="$t('media.picker.open')" @click.stop>
                        <v-icon size="15" color="#37474f">mdi-open-in-new</v-icon>
                    </a>
                    </div>
                </div>
            </div>

            <div class="mp-foot">
                <v-pagination v-if="pageCount > 1" v-model="page" :length="pageCount"
                    :total-visible="7" @input="reload" />
                <div class="mp-foot-right">
                    <v-btn text class="normal-case" @click="close">
                        {{ $t('button.cancel') }}
                    </v-btn>
                </div>
            </div>
        </div>
        <!-- ⚠ 新增完**直接選起來並關掉挑選器**：老師點「上傳」就是為了用它，
             再讓他從清單裡把剛上傳的那一筆找出來是多一步。 -->
        <MediaUploadDialog :show.sync="uploadOpen" :purpose="purpose" :source="uploadSource"
            @uploaded="pick" />
        <!-- 建完的字表同樣**直接選起來並關掉挑選器**（與上傳一致的理由）。
             ⚠ `v-if` 讓它只在字表的挑選器裡被建出來——它的 `show` watcher 是
             `immediate` 的，掛載時就會 reset，沒必要為別的 purpose 付這個成本。 -->
        <WordlistDialog v-if="canCreate" :show.sync="wordlistOpen" :purpose="purpose"
            @created="pick" />
        <!-- 熱區圖的唯讀預覽。⚠ 只在熱區的挑選器建出來。 -->
        <HotspotPreviewDialog v-if="purpose === 'hotspot'" :show.sync="previewOpen"
            :media="previewItem" />
    </v-dialog>
</template>

<script>
import { mediaServices } from '@/services/media';
import MediaUploadDialog from '@/components/console/diary/media/MediaUploadDialog';
import WordlistDialog from '@/components/console/diary/media/WordlistDialog';
import HotspotPreviewDialog from '@/components/console/diary/media/HotspotPreviewDialog';
import MediaThumb from '@/components/console/diary/media/MediaThumb';
import {
    isUploadable, isCreatable, providerLabelKey, attrsOf, wordsOf,
} from '@/components/console/diary/media/mediaConstants';

/**
 * 素材選擇器 — 見 mangachat-studio-api docs/plans/MediaLibrary.spec.md §7.1、§2.3。
 *
 * ⚠ **`purpose` 必填，而且不能拿掉。** 素材庫永遠是「為某個欄位挑東西」：挑找不同圖時
 * 若看得到封面圖，老師會選到沒有熱區的圖，學生端的遊戲當場壞掉（規格 §1）。
 *
 * ⚠ **三個 tab，沒有「團隊」。** team 共用已於 2026-08-20 全面取消。
 *
 * ⚠ **emit 兩個事件**：`input` 只帶 `media_id`（給 v-model 用），`select` 帶**整筆**素材。
 * 呼叫端幾乎都需要 `url`（要預覽）與 `attributes`（熱區、字表），只給 id 會逼每個呼叫端
 * 再打一次 API。
 */

/// 對應規格 §5.2 的 `scope` 參數。順序就是 tab 的順序。
const SCOPES = ['official', 'mine', 'public'];
/// 一頁 24 筆：格線是 4 欄，剛好 6 列。後端上限是 100。
const PER_PAGE = 24;

export default {
    name: 'MediaPicker',
    components: { MediaUploadDialog, WordlistDialog, HotspotPreviewDialog, MediaThumb },
    props: {
        /** 已選的 `media_id`（v-model）。 */
        value: { type: Number, default: null },
        /** 對話框開關，用 `.sync`。 */
        show: { type: Boolean, default: false },
        /** ⚠ 必填，見上面的說明。 */
        purpose: { type: String, required: true },
        /**
         * 縮圖框的比例，寫成 `寬/高`（例如 1:1 傳 `1`、16:9 傳 `16 / 9`）。
         *
         * ⚠⚠ **傳這一格圖片「將來會被畫成的比例」，不是圖片本身的比例。**
         * 需求方 2026-09-21：選圖時看不出來這張圖放進去會變什麼樣。
         * 例如選項圖在學生端是圓形的 1:1，就傳 `1`——老師在方形框裡看到一張
         * 很寬的圖，當下就知道左右會被切掉。
         *
         * ⚠ 不傳就用預設的 68%（約 3:2），那是通用瀏覽用的形狀。
         * ⚠ 熱區圖與字表**不受這個影響**（`usesFreeFit` 的分支照舊高度由內容決定），
         * 理由見 `usesFreeFit`。
         */
        ratio: { type: Number, default: null },
    },
    data() {
        return {
            scopes: SCOPES,
            /// 影片來源篩選。`''` = 不限。⚠ 只有 `purpose = 'video'` 用得到。
            provider: '',
            tabIndex: 0,
            keyword: '',
            items: [],
            total: 0,
            page: 1,
            isLoading: false,
            loadError: '',
            uploadOpen: false,
            uploadSource: 'file',
            wordlistOpen: false,
            previewOpen: false,
            previewItem: null,
        };
    },
    computed: {
        /**
         * 把 `ratio` 換成 `padding-top` 的百分比。
         * ⚠ 用 padding-top 而不是 `aspect-ratio`：後者 Safari 15 以前不支援，
         * 而這個框的撐高方式整份檔案都靠它（見 `.mp-fill` 的說明）。
         */
        thumbRatioStyle() {
            return this.ratio ? { paddingTop: `${(100 / this.ratio).toFixed(4)}%` } : null;
        },

        /** ⚠ 不認識的 purpose 要顯示原值而不是 raw key（規格 §2.5 的容忍規則）。 */
        purposeLabel() {
            const key = `media.purpose.${this.purpose}`;
            return this.$te(key) ? this.$t(key) : this.purpose;
        },
        /** 這個 purpose 能不能上傳。⚠ 字表不行——沒有檔案。 */
        canUpload() {
            return isUploadable(this.purpose);
        },
        /**
         * 這個 purpose 能不能**在介面上建立**（目前只有字表）。
         * ⚠ 與 `canUpload` 互斥：有檔案的素材上傳、沒檔案的素材建立，沒有第三種。
         */
        canCreate() {
            return isCreatable(this.purpose);
        },
        currentScope() {
            return SCOPES[this.tabIndex];
        },
        pageCount() {
            return Math.ceil(this.total / PER_PAGE) || 1;
        },
        isVideoPurpose() {
            return this.purpose === 'video';
        },
        emptyMessage() {
            /// 搜尋沒結果與「這個範圍本來就沒東西」是兩件事，講錯會讓老師以為素材不見了。
            /// ⚠ 來源篩選算在前者：篩掉之後空了，講「這個範圍沒有素材」同樣是在騙人。
            return (this.keyword || this.provider)
                ? this.$t('media.picker.empty_search')
                : this.$t(`media.picker.empty_${this.currentScope}`);
        },
    },
    watch: {
        /**
         * 開啟時才載入——關著的對話框不該打 API。
         *
         * ⚠ **`immediate: true` 不能省。** 呼叫端常常用 `v-if` 才把這個元件建出來
         * （例如 QuestModeSettings 是一個 picker 服務所有欄位，開啟時才決定 purpose）。
         * 那種情況下元件**掛載時 `show` 就已經是 true**，一般的 watcher 不會觸發
         * ——於是第一次打開是空的、第二次才正常。症狀是靜默的空清單，很難查。
         */
        show: {
            immediate: true,
            handler(opened) {
                if (opened) {
                    this.reload(1);
                }
            },
        },
        purpose() {
            if (this.show) {
                this.reload(1);
            }
        },
        /**
         * ⚠ 用 watch 而不是 `<v-tabs @change>`：Vuetify 2 的 VTabs 在**掛載時**就會
         * emit 一次 `change`，而 VDialog 是第一次開啟才 boot 內容——兩件事同時發生，
         * `@change` 會跟 `show` 的 watcher 一起各打一次 API。
         * watch 只在值真的變了才跑，天然沒有這個問題。
         */
        tabIndex() {
            if (this.show) {
                this.reload(1);
            }
        },
    },
    methods: {
        async reload(page) {
            this.page = page || 1;
            this.isLoading = true;
            this.loadError = '';
            try {
                const res = await mediaServices.getMediaList({
                    purpose: this.purpose,
                    scope: this.currentScope,
                    /// ⚠ **一律不送**（＝完全不過濾）。理由見 template 裡拿掉勾選框
                    /// 那一段：沒有任何寫入路徑會填 lang，而手動 insert 的素材有值，
                    /// 送了只會讓它們憑空消失。
                    q: this.keyword || null,
                    /// ⚠ 只在影片送——別的 purpose 的素材沒有 `provider`，
                    /// 送 `uploaded` 雖然等於不過濾，但那是靠巧合而不是靠設計。
                    provider: this.isVideoPurpose ? (this.provider || null) : null,
                    page: this.page,
                    perPage: PER_PAGE,
                    /// 只在「我的」要求連未處理完的一起列（後端也只在那個 scope 理它）
                    includeProcessing: this.currentScope === 'mine',
                });
                this.items = (res && res.items) || [];
                this.total = (res && res.total) || 0;
            } catch (err) {
                /// ⚠ 這個 catch 不能省。service 是 catch → log → throw，沒接就變成
                /// 「老師按了沒反應」——素材庫刻意不沿用那個缺口（規格 §9）。
                this.items = [];
                this.total = 0;
                /// 只有一種訊息：這支端點沒有 403 的路（驗證失敗是 401，而 401 由
                /// checkStatus 換頁去重新登入，根本不會走到這裡）。
                this.loadError = this.$t('media.error.load_failed');
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * 可以選嗎。
         *
         * ⚠ **只擋 `failed`**（2026-08-31 放寬，與存檔的驗證一致）。
         * `pending` / `processing` 可以選——存檔已經允許引用轉檔中的影片
         * （規格 §8），那挑選器擋住它就自相矛盾：老師上傳完的影片會選不起來。
         * 狀態標籤照樣顯示，讓他知道學生要等一下才看得到。
         *
         * ⚠ `failed` 的影片**永遠不會**好，選了就是永久壞掉的引用。
         *
         * ⚠ `status` 不用看：已下架的素材在後端查詢就被過濾掉了（規格 §2.3）。
         */
        isSelectable(item) {
            return item.process_status !== 'failed';
        },

        /** ⚠ 不認識的狀態顯示原值，不要顯示 raw key（規格 §2.5 的容忍規則）。 */
        processLabel(item) {
            const key = `media.process.${item.process_status}`;
            return this.$te(key) ? this.$t(key) : item.process_status;
        },

        /** 影片縮圖，沒有就回空字串（退回下面的圖示 + 檔名）。 */
        providerLabelKey,


        /**
         * 開熱區的唯讀預覽。
         * ⚠ 一定要在開啟**之前**設好 `previewItem`——對話框讀的是 prop，
         * 順序反了會顯示上一次那一筆。
         */
        openPreview(item) {
            this.previewItem = item;
            this.previewOpen = true;
        },

        openUpload(source) {
            this.uploadSource = source;
            this.uploadOpen = true;
        },

        pick(item) {
            /// 保險：`disabled` 已經擋住了，但 pick 也可能被別的路徑呼叫
            if (!this.isSelectable(item)) {
                return;
            }
            this.$emit('input', item.media_id);
            this.$emit('select', item);
            this.close();
        },

        close() {
            this.$emit('update:show', false);
        },


        /// ⚠ 只給下面那個「N 個熱區」的計數用——框本身由 MediaThumb 畫。
        targetsOf(item) {
            const targets = attrsOf(item).targets;
            return Array.isArray(targets) ? targets : [];
        },
        /// ⚠ 同上：只給計數用。`mediaConstants` 的共用版（本地那份重複的已移除）。
        wordsOf,


        /**
         * 這一筆的縮圖要不要用「高度由內容決定」的排法。
         *
         * 熱區圖必須用它：熱區的百分比座標是相對於圖片本身，contain 會讓框整體偏移。
         * 字表也用它：沒有檔案可看，列出的單字自己決定高度。
         *
         * 這個判斷同時決定三件事（縮圖框的 padding-top、MediaThumb 的 fit、
         * 要不要絕對定位），所以收成一個函式——分開寫遲早只改到其中一兩處。
         */
        usesFreeFit(item) {
            return this.isHotspot(item) || this.isWordlist(item);
        },

        isHotspot(item) {
            /// 判斷用 purpose 而不是「有沒有 targets」：targets 為空的熱區圖也要看得出來
            /// 是熱區圖（而且要看得出它是空的，那正是老師不該選它的理由）。
            return item.purpose === 'hotspot' && !!item.url;
        },

        isWordlist(item) {
            /// ⚠ 這裡刻意用 `kind` 而不是 `purpose`（跟 isHotspot 相反）。字表只有
            /// wordsearch / colorword 兩個 purpose，但**未來會有更多**——用 kind 判斷，
            /// 不認識的新字表 purpose 也還是列得出單字（規格 §2.5 的容忍規則）。
            /// 熱區反過來：kind 是 image，跟封面圖分不開，只能靠 purpose。
            return item.kind === 'wordlist';
        },

    },
};
</script>

<style scoped>
.mp {
    background: #fff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    max-height: 84vh;
}

.mp-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 10px;
}

.mp-title {
    font-size: 18px;
    font-weight: 500;
    color: #212121;
}

.mp-sub {
    font-size: 13px;
    color: #757575;
    margin-top: 2px;
}

/* tab 在左、新增入口在右，共用同一條底線 */
.mp-tabrow {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 12px;
    border-bottom: 1px solid #e0e0e0;
    padding-right: 16px;
}

.mp-tabs {
    flex: 1 1 auto;
    min-width: 0;
}

.mp-actions {
    flex: 0 0 auto;
    display: flex;
    gap: 8px;
}

.mp-act {
    font-size: 13px;
    color: #1a73e8;
    background: #fff;
    border: 1px solid #90caf9;
    border-radius: 4px;
    padding: 0 12px;
    height: 30px;
    cursor: pointer;
    white-space: nowrap;
}

.mp-act:hover {
    background: #f0f6fe;
}

/* 與搜尋框同高，兩個控制項才對得齊 */
.mp-provider .v-btn {
    height: 40px !important;
}

.mp-filters {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 20px;
    flex-wrap: wrap;
}

.mp-search {
    max-width: 320px;
}

.mp-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 4px 20px 12px;
    min-height: 260px;
}

.mp-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 250px;
}

.mp-state-msg {
    font-size: 14px;
    color: #757575;
    text-align: center;
}

.mp-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
}

/* 卡片的定位容器。⚠ 它自己不能有 padding/border——`.mp-grid` 的格子尺寸是給卡片的，
   多一層框會讓卡片之間的間距看起來不一致。 */
.mp-cell {
    position: relative;
    min-width: 0;
}

/* 「開啟」疊在縮圖右上角。⚠ 不放在卡片內部是因為 `<a>` 不能巢在 `<button>` 裡。
   ⚠ 半透明白底 + 邊框兩者都要：純圖示在淺色縮圖上看不見。 */
/* ⚠ 這個 class 同時給 `<a>`（新分頁）與 `<button>`（站內預覽）用，
   所以要自己歸零 button 的預設樣式（padding/font），否則兩者大小不一致。 */
.mp-open {
    padding: 0;
    font: inherit;
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid #cfd8dc;
    /* 蓋在卡片上，但要低於「已選」的勾勾（那在左上角，不會重疊，這只是保險） */
    z-index: 1;
}

.mp-open:hover {
    background: #fff;
    border-color: #1e88e5;
}

.mp-card {
    text-align: left;
    /* grid item 預設 min-width:auto，長標題會把欄撐寬而不是讓 .mp-name 出現省略號。
       比照 FrameTypePicker.vue 的 .tp-body。
       ⚠ 現在 grid 的子項是 `.mp-cell`（那一層也設了 min-width: 0），
       卡片要 `width: 100%` 才會撐滿格子。 */
    min-width: 0;
    width: 100%;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 6px;
    cursor: pointer;
}

.mp-card:hover {
    border-color: #1e88e5;
}

.mp-card--on {
    border-color: #1e88e5;
    box-shadow: 0 0 0 1px #1e88e5 inset;
}

.mp-thumb {
    position: relative;
    width: 100%;
    padding-top: 68%;
    background: #eaf1f4;
    border-radius: 4px;
    overflow: hidden;
}


/* 高度由內容決定的縮圖框：熱區圖與字表用。
   熱區的百分比座標是相對於圖片本身，所以圖片必須撐滿並定義這個框的高度，
   不能留白置中，否則框會整體偏移。 */
/* ⚠ 要 `!important`：比例是綁在 style 屬性上的（`thumbRatioStyle`），
   熱區與字表那條分支雖然已經傳 null 了，這裡再擋一次，
   免得日後有人把 style 改成無條件套用就悄悄破壞熱區的座標對齊。 */
.mp-thumb--free {
    padding-top: 0 !important;
}

.mp-dim {
    font-size: 11px;
    color: #90a4ae;
    line-height: 14px;
}

/* 填滿 .mp-thumb 這種「用 padding-top 撐出比例」的框。

   一定要絕對定位：那種框的 content 高度是 auto（高度來自 padding），而 CSS 規定
   容器高度依內容決定時，子元素的百分比高度視為 auto。所以 MediaThumb 根元素的
   height:100% 解析不出來，圖片會以原始比例排在 padding 底下，格子被撐得很高、
   上半部是一片空白的底色。

   2026-09-08 修：抽 MediaThumb 時漏掉了原本 .mp-img 帶的這四行（回報時的症狀是
   影片縮圖特別明顯，但圖片與 PDF 一樣中招）。
   另外兩個呼叫端不需要這個 class，因為它們的容器有寫死的高度（我的素材 54px、
   題目編輯器的素材欄 78px），百分比高度解析得出來。 */
.mp-fill {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
}



.mp-meta {
    font-size: 11px;
    color: #607d8b;
    margin-top: 2px;
}






.mp-check {
    position: absolute;
    top: 4px;
    right: 4px;
    background: #fff;
    border-radius: 50%;
}

.mp-card--off {
    opacity: 0.55;
    cursor: not-allowed;
}

.mp-status {
    font-size: 11px;
    color: #ef6c00;
    font-weight: 600;
    margin-top: 2px;
}

.mp-status--bad {
    color: #c62828;
}

.mp-name {
    font-size: 13px;
    color: #212121;
    margin-top: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mp-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 16px 12px;
    border-top: 1px solid #e0e0e0;
}

.mp-foot-right {
    margin-left: auto;
}

@media (max-width: 767px) {
    .mp-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .mp-search {
        max-width: none;
    }
}
</style>
